import React, { useEffect, useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';

const MarketPage: React.FC = () => {
  const { token, refreshMe, user } = useAuth();
  const [stock, setStock] = useState<Array<{ itemId: string; name: string; qty: number; price: number }>>([]);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ ok: boolean; stock: any[] }>('/market/stock').then((res) => setStock(res.stock || []));
  }, []);

  const buyStock = async (itemId: string, qtyBuy: number) => {
    setMsg(null);
    try {
      await api.post('/market/stock/buy', { itemId, qty: qtyBuy }, token || undefined);
      setMsg('Purchased successfully');
      const res = await api.get<{ ok: boolean; stock: any[] }>('/market/stock');
      setStock(res.stock || []);
      await refreshMe();
    } catch (err: any) {
      setMsg(err?.message || 'Purchase failed');
    }
  };

  const sellProduce = async (cropType: string, qtySell: number) => {
    setMsg(null);
    try {
      const res = await api.post<{ ok: boolean; balance: number; unitPrice: number; seedItem: { itemId: string; name: string; qty: number; price: number } }>(
        '/market/stock/sell',
        { cropType, qty: qtySell },
        token || undefined
      );
      setMsg(`Sold ${qtySell} ${cropType} at $${res.unitPrice}. New balance: $${Number(res.balance).toFixed(2)}`);
      await refreshMe();
      const res2 = await api.get<{ ok: boolean; stock: any[] }>('/market/stock');
      setStock(res2.stock || []);
    } catch (err: any) {
      setMsg(err?.message || 'Sell failed');
    }
  };

  return (
    <div className="space-y-6">
      <div className="p-5 rounded-xl bg-gray-900/70 border border-gray-800">
        <h2 className="text-xl font-semibold mb-3 flex items-center gap-2"><i className="fas fa-store text-sky-400"></i> <span>Direct Market</span></h2>
        <div className="space-y-6">
          <section>
            <h4 className="font-semibold mb-2 flex items-center gap-2"><i className="fas fa-seedling text-green-400"></i> <span>Seeds</span></h4>
            <div className="grid md:grid-cols-3 gap-3">
              {stock.filter((s)=>s.itemId.startsWith('seed-')).map((s)=>RenderStockItem(s, buyStock))}
            </div>
          </section>
          <section>
            <h4 className="font-semibold mb-2 flex items-center gap-2"><i className="fas fa-flask text-amber-400"></i> <span>Fertilizers</span></h4>
            <div className="grid md:grid-cols-3 gap-3">
              {stock.filter((s)=>s.itemId.startsWith('fert-')).map((s)=>RenderStockItem(s, buyStock))}
            </div>
          </section>
          <section>
            <h4 className="font-semibold mb-2 flex items-center gap-2"><i className="fas fa-tools text-purple-400"></i> <span>Tools</span></h4>
            <div className="grid md:grid-cols-3 gap-3">
              {stock.filter((s)=>s.itemId.startsWith('tool-')).map((s)=>RenderStockItem(s, buyStock))}
            </div>
          </section>
          <section>
            <h4 className="font-semibold mb-2 flex items-center gap-2"><i className="fas fa-shopping-basket text-emerald-400"></i> <span>Sell Produce</span></h4>
            <div className="grid md:grid-cols-2 gap-3">
              {((user?.inventory || []).filter((i)=>i.itemId.startsWith('crop-'))).map((i)=>RenderSellItem(i, sellProduce))}
            </div>
            <div className="text-xs text-gray-400 mt-2">Market auto-prices based on seed availability. Selling produce increases seed stock.</div>
          </section>
        </div>
        {msg && <div className="mt-3 text-sm text-gray-300">{msg}</div>}
      </div>
    </div>
  );
};

function RenderStockItem(s: { itemId: string; name: string; qty: number; price: number }, buyStock: (itemId: string, qty: number) => void) {
  return (
    <div key={s.itemId} className="p-3 rounded bg-gray-800">
      <div className="flex justify-between">
        <span className="font-medium">{s.name}</span>
        <span>${s.price}</span>
      </div>
      <div className="text-xs text-gray-400">In stock: {s.qty}</div>
      <div className="mt-2 flex gap-2">
        <input className="px-2 py-1 rounded bg-gray-700 text-white w-20" type="number" min={1} defaultValue={10} id={`qty-${s.itemId}`} />
        <button className="px-3 py-1 rounded bg-green-600 hover:bg-green-500" onClick={() => {
          const el = document.getElementById(`qty-${s.itemId}`) as HTMLInputElement;
          const q = parseInt(el?.value || '1');
          buyStock(s.itemId, q);
        }}>Buy</button>
      </div>
    </div>
  );
}

function RenderSellItem(i: { itemId: string; name: string; qty: number }, sellProduce: (cropType: string, qty: number) => void) {
  const cropType = (i.name || '').toLowerCase();
  const idQty = `sell-qty-${i.itemId}`;
  return (
    <div key={i.itemId} className="p-3 rounded bg-gray-800">
      <div className="flex justify-between">
        <span className="font-medium">{i.name}</span>
        <span className="text-xs text-gray-400">Owned: {i.qty}</span>
      </div>
      <div className="mt-2 flex gap-2 items-center">
        <input className="px-2 py-1 rounded bg-gray-700 text-white w-20" type="number" min={1} defaultValue={Math.min(10, i.qty)} id={idQty} />
        <button className="px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-500" onClick={() => {
          const qEl = document.getElementById(idQty) as HTMLInputElement;
          const q = parseInt(qEl?.value || '1');
          sellProduce(cropType.split(' ')[0], q);
        }}>Sell</button>
      </div>
    </div>
  );
}

export default MarketPage;